/** Artists + aliases CRUD (WS1 routes). Edits send If-Match: version; a 409 shows the conflict with Reload.
 * Create / edit / aliases need the `admin` scope (read-only otherwise); Scan now needs `make`. */
import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { api, ifMatch, type Schemas, unwrap } from "../../api/client";
import { qk } from "../../api/keys";
import { useScope } from "../../lib/me";
import { useArtists } from "../board/BoardView";
import { useAction } from "../lib/actions";
import { ActionError, QueryView, Section, TableScroll } from "../lib/ui";
import { intParam, useUrlState } from "../lib/url";
import { AdminOnlyNote } from "./AdminOnly";

type Artist = Schemas["ArtistOut"];
type AliasKind = Schemas["AliasKind"];
const ALIAS_KINDS: AliasKind[] = ["name", "spotify", "youtube_channel", "youtube_topic", "youtube_label", "tiktok_handle",
  "genius", "namesake_block"];

type Draft = { name: string; active: boolean; include_feats: boolean; mv_count: string; song_count: string; stamp: string };
const draftOf = (a?: Artist): Draft => ({
  name: a?.name ?? "", active: a?.active ?? true, include_feats: a?.include_feats ?? true,
  mv_count: String(a?.mv_count ?? 4), song_count: String(a?.song_count ?? 10), stamp: a?.stamp ?? "",
});

/** Client-side checks mirror the API limits; the server still validates (422 lists every problem). */
export function draftProblems(d: Draft): string[] {
  const out: string[] = [];
  if (!d.name.trim()) out.push("name: required");
  const mv = intParam(d.mv_count);
  if (mv === undefined || mv < 1 || mv > 50) out.push("mv_count: whole number 1–50");
  const sc = intParam(d.song_count);
  if (sc === undefined || sc < 1 || sc > 100) out.push("song_count: whole number 1–100");
  return out;
}

function DraftFields({ d, set, idPrefix }: { d: Draft; set: (d: Draft) => void; idPrefix: string }) {
  return (
    <div className="ws7-toolbar">
      <label className="ws7-field">
        Name
        <input value={d.name} data-testid={`${idPrefix}-name`} onChange={(e) => set({ ...d, name: e.target.value })} />
      </label>
      <label className="ws7-field">
        Newest MVs
        <input type="number" min={1} max={50} step={1} value={d.mv_count} data-testid={`${idPrefix}-mv`}
          onChange={(e) => set({ ...d, mv_count: e.target.value })} />
      </label>
      <label className="ws7-field">
        Newest songs
        <input type="number" min={1} max={100} step={1} value={d.song_count} data-testid={`${idPrefix}-songs`}
          onChange={(e) => set({ ...d, song_count: e.target.value })} />
      </label>
      <label className="ws7-field">
        Stamp
        <input value={d.stamp} data-testid={`${idPrefix}-stamp`} onChange={(e) => set({ ...d, stamp: e.target.value })} />
      </label>
      <label className="ws7-check">
        <input type="checkbox" checked={d.include_feats} data-testid={`${idPrefix}-feats`}
          onChange={(e) => set({ ...d, include_feats: e.target.checked })} />
        Include feats
      </label>
      <label className="ws7-check">
        <input type="checkbox" checked={d.active} data-testid={`${idPrefix}-active`} onChange={(e) => set({ ...d, active: e.target.checked })} />
        Active
      </label>
    </div>
  );
}

function EditArtist({ a, onClose }: { a: Artist; onClose: () => void }) {
  const qc = useQueryClient();
  const [d, setD] = useState(draftOf(a));
  const problems = draftProblems(d);
  const save = useAction(
    () => unwrap(api.PATCH("/api/artists/{artist_id}", {
      params: { path: { artist_id: a.id } },
      body: { name: d.name.trim(), active: d.active, include_feats: d.include_feats, mv_count: Number(d.mv_count),
        song_count: Number(d.song_count), stamp: d.stamp.trim() || null },
      headers: ifMatch(a.version),
    })),
    { label: `Save artist ${a.name}`, success: (r) => `Saved artist ${r.name} (version ${r.version})`, invalidate: [qk.list("artists")] },
  );
  return (
    <form className="ws7-form" aria-label={`Edit artist ${a.name}`} onSubmit={async (e: FormEvent) => {
      e.preventDefault();
      if (problems.length) return;
      if (await save.run()) onClose();
    }}>
      <DraftFields d={d} set={setD} idPrefix={`artist-${a.id}`} />
      {problems.length > 0 && <ul className="ws7-field-error" data-testid="artist-problems">{problems.map((p) => <li key={p}>{p}</li>)}</ul>}
      <ActionError error={save.error} onReload={async () => {
        await qc.invalidateQueries({ queryKey: qk.list("artists") });
        save.clear();
        onClose();
      }} />
      <div className="ws7-row">
        <button type="submit" className="ws7-primary" data-testid={`artist-save-${a.id}`} disabled={save.pending || problems.length > 0}>
          Save {a.name}
        </button>
        <button type="button" data-testid={`artist-cancel-${a.id}`} onClick={onClose}>Cancel</button>
      </div>
    </form>
  );
}

function Aliases({ a, admin }: { a: Artist; admin: boolean }) {
  const [kind, setKind] = useState<AliasKind>("name");
  const [value, setValue] = useState("");
  const add = useAction(
    () => unwrap(api.POST("/api/artists/{artist_id}/aliases", { params: { path: { artist_id: a.id } }, body: { kind, value: value.trim() } })),
    { label: `Add alias to ${a.name}`, success: (r) => `Added alias ${r.kind}:${r.value} to ${a.name}`, invalidate: [qk.list("artists")] },
  );
  const del = useAction(
    (al: Schemas["AliasOut"]) => unwrap(api.DELETE("/api/aliases/{alias_id}", { params: { path: { alias_id: al.id } } })),
    { label: (al) => `Remove alias ${al.value}`, success: (_r, al) => `Removed alias ${al.kind}:${al.value} from ${a.name}`, invalidate: [qk.list("artists")] },
  );
  return (
    <div>
      <ul className="ws7-chips" aria-label={`Aliases of ${a.name}`}>
        {a.aliases.map((al) => (
          <li key={al.id} className="chip">
            <span className="ws7-muted">{al.kind}:</span> {al.value}{" "}
            {admin && (
              <button type="button" data-testid={`alias-remove-${al.id}`} aria-label={`Remove alias ${al.kind} ${al.value} of ${a.name}`}
                disabled={del.pending} onClick={() => void del.run(al)}>
                Remove
              </button>
            )}
          </li>
        ))}
      </ul>
      {admin && <form className="ws7-inline" aria-label={`Add alias to ${a.name}`} onSubmit={async (e) => {
        e.preventDefault();
        if (value.trim() && (await add.run())) setValue("");
      }}>
        <label className="ws7-field">
          Alias kind
          <select value={kind} data-testid={`alias-kind-${a.id}`} onChange={(e) => setKind(e.target.value as AliasKind)}>
            {ALIAS_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </label>
        <label className="ws7-field">
          Alias value
          <input value={value} data-testid={`alias-value-${a.id}`} onChange={(e) => setValue(e.target.value)} />
        </label>
        <button type="submit" data-testid={`alias-add-${a.id}`} aria-label={`Add alias to ${a.name}`} disabled={!value.trim() || add.pending}>
          Add alias
        </button>
      </form>}
      <ActionError error={add.error ?? del.error} />
    </div>
  );
}

function CreateArtist() {
  const [d, setD] = useState(draftOf());
  const problems = draftProblems(d);
  const create = useAction(
    () => unwrap(api.POST("/api/artists", { body: { name: d.name.trim(), include_feats: d.include_feats, mv_count: Number(d.mv_count),
      song_count: Number(d.song_count), stamp: d.stamp.trim() || null, aliases: [] } })),
    { label: "Create artist", success: (r) => `Created artist ${r.name} (#${r.id})`, invalidate: [qk.list("artists")] },
  );
  return (
    <form className="ws7-form" aria-label="Create artist" onSubmit={async (e) => {
      e.preventDefault();
      if (!problems.length && (await create.run())) setD(draftOf());
    }}>
      <DraftFields d={d} set={setD} idPrefix="artist-new" />
      <div className="ws7-row">
        <button type="submit" className="ws7-primary" data-testid="artist-create" disabled={create.pending || problems.length > 0}>
          Create artist
        </button>
        {d.name && problems.length > 0 && <span className="ws7-field-error">{problems.join("; ")}</span>}
      </div>
      <ActionError error={create.error} />
    </form>
  );
}

export function ArtistsTab({ admin }: { admin: boolean }) {
  const artists = useArtists();
  const maker = useScope("make");
  const { sp, set } = useUrlState();
  const editing = intParam(sp.get("edit"));
  const scan = useAction(
    (a: Artist) => unwrap(api.POST("/api/artists/{artist_id}/scan", { params: { path: { artist_id: a.id } } })),
    { label: (a) => `Scan ${a.name}`, success: (r, a) => `Queued scan of ${a.name}: jobs ${r.job_ids.map((j) => `#${j}`).join(", ") || "none"}` },
  );
  return (
    <div className="ws7-page">
      <Section id="artists" title="Artists">
        {!admin && <AdminOnlyNote what="adding and editing artists and aliases" testId="artists-admin-only" />}
        <ActionError error={scan.error} />
        <QueryView q={artists} what="artists">
          {(rows) => (
            <TableScroll label="Artist table">
              <table className="ws7-table" aria-label="Artists" data-testid="artists-table">
                <thead>
                  <tr>
                    <th scope="col">Artist</th>
                    <th scope="col">Board</th>
                    <th scope="col">Aliases</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((a) => (
                    <tr key={a.id} data-testid={`artist-row-${a.id}`}>
                      <td>
                        {admin && editing === a.id ? (
                          <EditArtist a={a} onClose={() => set({ edit: undefined })} />
                        ) : (
                          <>
                            <strong>{a.name}</strong> <span className="ws7-muted">#{a.id} · v{a.version}</span>
                            <div className="ws7-muted">{a.active ? "active" : "inactive"} · stamp {a.stamp ?? "none"}</div>
                          </>
                        )}
                      </td>
                      <td>
                        newest {a.mv_count} MVs, {a.song_count} songs, feats {a.include_feats ? "included" : "excluded"}
                        <div className="ws7-muted">{a.counts.videos} videos · {a.counts.songs} songs</div>
                      </td>
                      <td><Aliases a={a} admin={admin} /></td>
                      <td>
                        <span className="ws7-inline">
                          {admin && editing !== a.id && (
                            <button type="button" data-testid={`artist-edit-${a.id}`} aria-label={`Edit artist ${a.name}`}
                              onClick={() => set({ edit: a.id })}>
                              Edit
                            </button>
                          )}
                          {maker && (
                            <button type="button" data-testid={`artist-scan-${a.id}`} aria-label={`Scan ${a.name} now`}
                              disabled={scan.pending} onClick={() => void scan.run(a)}>
                              Scan now
                            </button>
                          )}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          )}
        </QueryView>
      </Section>
      {admin && (
        <Section id="artist-create" title="Add an artist">
          <CreateArtist />
        </Section>
      )}
    </div>
  );
}

